import { useEffect, useRef, useState } from 'react'

import { type PercentCrop } from 'react-image-crop'

import { trackEvent } from '@/utils/analytics'

import { cropAndResizeImage, isFullImageCrop } from '../utils/cropUtils'
import { convertJpgToPng, resizePng } from '../utils/imageUtils'
import { convertPdfPageToPng, getPdfPageCount } from '../utils/pdfUtils'
import { isJpgFile, isPdfFile, isPngFile } from '../utils/utils'

const PREVIEW_MAX_DIMENSION = 800
const FULL_IMAGE_CROP: PercentCrop = {
  x: 0,
  y: 0,
  width: 100,
  height: 100,
  unit: '%',
}

interface UseFloorPlanUploadProcessingIProps {
  fileUpload: FileList | undefined
}

export const useFloorPlanUploadProcessing = ({
  fileUpload,
}: UseFloorPlanUploadProcessingIProps) => {
  const [selectedPdfPage, setSelectedPdfPage] = useState(1)
  const [numPdfPages, setNumPdfPages] = useState(0)
  const [isPageConfirmed, setIsPageConfirmed] = useState(false)
  const [crop, setCrop] = useState<PercentCrop | undefined>(FULL_IMAGE_CROP)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | undefined>(
    undefined
  )
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const pdfSelectorRef = useRef<HTMLDivElement>(null)
  const cropperRef = useRef<HTMLDivElement>(null)

  // Derived state
  const selectedFile = fileUpload?.item(0) ?? undefined
  const isPdf = selectedFile ? isPdfFile(selectedFile) : false
  const shouldShowCropper =
    !!selectedFile && !!imagePreviewUrl && (!isPdf || isPageConfirmed)

  const resetPdfPage = () => setSelectedPdfPage(1)
  const resetCrop = () => setCrop(FULL_IMAGE_CROP)
  const confirmPage = () => setIsPageConfirmed(true)
  const unconfirmPage = () => {
    setIsPageConfirmed(false)
    resetCrop()
  }

  // Reset page and crop when file changes; fetch PDF page count
  useEffect(() => {
    resetPdfPage()
    resetCrop()
    setNumPdfPages(0)
    setIsPageConfirmed(false)

    if (selectedFile && isPdfFile(selectedFile)) {
      let isCancelled = false
      getPdfPageCount(selectedFile).then((count) => {
        if (!isCancelled) {
          setNumPdfPages(count)
          // Auto-confirm single-page PDFs — nothing to select
          if (count === 1) setIsPageConfirmed(true)
        }
      })
      return () => {
        isCancelled = true
      }
    }
  }, [selectedFile])

  // Reset crop when PDF page changes
  useEffect(() => {
    resetCrop()
  }, [selectedPdfPage])

  // Generate image preview URL for the cropper
  useEffect(() => {
    let isCancelled = false
    let currentUrl: string | undefined

    const generatePreview = async () => {
      if (!selectedFile) {
        setImagePreviewUrl(undefined)
        setIsPreviewLoading(false)
        return
      }

      if (isPdfFile(selectedFile)) {
        // Defer PDF preview until page is confirmed
        if (!isPageConfirmed) {
          setImagePreviewUrl(undefined)
          setIsPreviewLoading(false)
          return
        }
        setIsPreviewLoading(true)
        try {
          const previewFile = await convertPdfPageToPng({
            pdfFile: selectedFile,
            pageNumber: selectedPdfPage,
            maxDimension: PREVIEW_MAX_DIMENSION,
          })
          if (!isCancelled) {
            currentUrl = URL.createObjectURL(previewFile)
            setImagePreviewUrl(currentUrl)
          }
        } catch {
          if (!isCancelled) setImagePreviewUrl(undefined)
        } finally {
          if (!isCancelled) setIsPreviewLoading(false)
        }
      } else {
        if (!isCancelled) {
          currentUrl = URL.createObjectURL(selectedFile)
          setImagePreviewUrl(currentUrl)
        }
      }
    }

    generatePreview()

    return () => {
      isCancelled = true
      if (currentUrl) URL.revokeObjectURL(currentUrl)
    }
  }, [selectedFile, selectedPdfPage, isPageConfirmed])

  // Scroll to PDF selector when PDF is attached
  useEffect(() => {
    if (isPdf) {
      // Small delay to allow PdfPageSelector to render before scrolling
      const timeoutId = setTimeout(() => {
        pdfSelectorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [isPdf])

  // Scroll to cropper when preview URL is ready (non-PDF files)
  useEffect(() => {
    if (shouldShowCropper && !isPdf) {
      const timeoutId = setTimeout(() => {
        cropperRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [shouldShowCropper, isPdf])

  // Convert PDF or JPG to PNG if needed, apply crop, resize, otherwise return original file
  const getFileForUpload = async (
    file: File | undefined
  ): Promise<File | undefined> => {
    if (!file) return undefined

    const didCrop = !!crop && !isFullImageCrop(crop)
    const fileType = isPdfFile(file)
      ? 'PDF'
      : isJpgFile(file)
      ? 'JPG'
      : isPngFile(file)
      ? 'PNG'
      : 'Other'

    trackEvent('FloorPlanUpload', {
      fileType,
      didCrop,
    })

    let sourceFile: File = file

    // Step 1: Convert PDF to PNG at full resolution
    if (isPdfFile(file)) {
      sourceFile = await convertPdfPageToPng({
        pdfFile: file,
        pageNumber: selectedPdfPage,
      })
    }

    // Step 2: Crop + resize if crop is set
    if (didCrop) {
      return cropAndResizeImage(sourceFile, crop)
    }

    // No crop — existing behavior
    if (isPdfFile(file)) return sourceFile
    if (isJpgFile(file)) return convertJpgToPng(file)
    if (isPngFile(file)) return resizePng(file)
    return file
  }

  return {
    selectedPdfPage,
    setSelectedPdfPage,
    numPdfPages,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    resetPdfPage,
    getFileForUpload,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
  }
}
